import { useState } from 'react';
import { FileTrigger } from 'react-aria-components';
import { Button, Flex, Text } from '@backstage/ui';
import { CustomDialog } from '@internal/backstage-plugin-devops-react';
import { AzureLoadFiles } from '../Providers';

interface AzureLoadFileDialogProps {
  isOpen: boolean;
  onSubmit: (files: AzureLoadFiles) => void;
  onClose: () => void;
}

const readFile = async (file: File | undefined) => {
  if (!file) {
    return undefined;
  }
  const content = await file.text();
  try {
    JSON.parse(content);
  } catch {
    throw new Error(`File '${file.name}' is not a valid JSON file`);
  }
  return { fileName: file.name, content };
};

export function AzureLoadFileDialog(props: Readonly<AzureLoadFileDialogProps>) {
  const [inventoryFile, setInventoryFile] = useState<File | undefined>();
  const [reportFile, setReportFile] = useState<File | undefined>();
  const [error, setError] = useState<string | undefined>();

  const onClose = () => {
    setInventoryFile(undefined);
    setReportFile(undefined);
    setError(undefined);
    props.onClose();
  };

  const onSubmit = async () => {
    if (!inventoryFile) {
      setError('Inventory file is required');
      return;
    }
    try {
      const inventory = await readFile(inventoryFile);
      const report = await readFile(reportFile);
      if (!inventory) {
        setError('Inventory file is required');
        return;
      }
      props.onSubmit({ inventory, report });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const filePicker = (
    label: string,
    file: File | undefined,
    onSelect: (file: File | undefined) => void,
  ) => (
    <Flex direction="column" gap="2">
      <Text as="label" weight="bold">
        {label}
      </Text>
      <Flex gap="2" align="center">
        <FileTrigger
          acceptedFileTypes={['.json', 'application/json']}
          onSelect={files => onSelect(files?.[0])}
        >
          <Button variant="secondary" type="button">
            Select file
          </Button>
        </FileTrigger>
        <Text>{file ? file.name : 'No file selected'}</Text>
      </Flex>
    </Flex>
  );

  return (
    <CustomDialog
      isOpen={props.isOpen}
      title="Load Azure Scan Results"
      submitButtonText="Load"
      onClose={onClose}
      onSubmit={onSubmit}
    >
      <Flex direction="column" gap="4">
        <Text>
          Upload inventory and report files exported by a local CloudTools
          Azure scan. The inventory file is required.
        </Text>
        {filePicker(
          'Inventory file (required)',
          inventoryFile,
          setInventoryFile,
        )}
        {filePicker('Report file (optional)', reportFile, setReportFile)}
        {error && <Text color="danger">{error}</Text>}
      </Flex>
    </CustomDialog>
  );
}
